import { useCallback, useState, useEffect } from 'react';
import { useAvailableWorkersStore } from '@core/availableWorkers/useAvailableWorkersStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { Job, JobStatus, User } from '@core/generated/gql-types';

interface UseAvailableWorkersProps {
  job: Job | null;
}

const useAvailableWorkers = ({ job }: UseAvailableWorkersProps) => {
  const [availableWorkers, setAvailableWorkers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { fetchUsersAvailableForJob } = useAvailableWorkersStore();

  const { getWorkersByIds } = useWorkerStore();

  const fetchAvailableWorkersForJob = useCallback(
    async (jobId: string) => {
      try {
        if (!jobId) return [];

        setIsLoading(true);
        const availableWorkerIds = await fetchUsersAvailableForJob(jobId);
        const availableWorkers = getWorkersByIds(availableWorkerIds);
        setAvailableWorkers(availableWorkers);
      } catch (error) {
        console.error('Error fetching available workers for job:', error);
        setAvailableWorkers([]);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchUsersAvailableForJob, getWorkersByIds],
  );

  useEffect(() => {
    if (!job?.id || job.itemStatus === JobStatus.Cancelled) {
      setAvailableWorkers([]);
      return;
    }

    setAvailableWorkers([]);
    void fetchAvailableWorkersForJob(job.id);
  }, [fetchAvailableWorkersForJob, job?.id, job?.itemStatus]);

  return { availableWorkers, isLoading };
};

export default useAvailableWorkers;
